import React, {useState} from "react"
import useAsyncResultIdle from "../../../hooks/useAsyncResultIdle"
import {Account} from "../../../resources/account"
import {useAccessToken} from "../../../services/auth"

import {Address} from "../../../resources/common"
import {CheckPayment, createClosureCheckPayment} from "../../../resources/checkPayments"
import Icon from "../../../components/Icon/Icon"
import {KeyValueCard, KeyValueCardContainer} from "../../../components/KeyValueCard/KeyValueCard"
import {Modal} from "../../../components/Modals/Modal"
import Button from "../../../components/Button/Button"
import moment from "moment"
import CheckMailingAddress from "./CheckMailingAddress"
import {handleSubmit, refreshAndClose} from "./Utils"

interface ReIssueClosureCheckModalProps {
	account: Account
	customerId: string
	updatedAddress: Address
	openChangeAddressModal: () => void
	existingCheck: CheckPayment
	closeModal: () => void
	onSuccess: () => void
}

export function ReIssueClosureCheckModal({
	account,
	customerId,
	updatedAddress,
	openChangeAddressModal,
	existingCheck,
	closeModal,
	onSuccess,
}: ReIssueClosureCheckModalProps) {
	const accessToken = useAccessToken()
	const accountId = account.id
	const [, create] = useAsyncResultIdle(createClosureCheckPayment)

	const [confirmStop, setConfirmStop] = useState(false)
	const [isLoading, setLoading] = useState(false)
	const [successMessage, setSuccessMessage] = React.useState("")
	const [errorMessage, setErrorMessage] = React.useState("")

	const onSubmit = () => {
		handleSubmit({
			createFn: create,
			params: {accessToken, accountId, customerId, addressOverride: updatedAddress},
			setLoading,
			setSuccessMessage,
			setErrorMessage,
			onSuccess,
			closeModal,
			successMessage: "Check re-issued successfully",
		})
	}

	return (
		<Modal
			classname="closure-check-modal"
			close={() => refreshAndClose(onSuccess, closeModal)}
			enableClickAway={true}
			blockBodyScroll={true}
		>
			<div className="modal-content re-issue-closure-check">
				<h3 className="title is-4 text-closure-check">Confirm Account Closure Check Reissuance</h3>
				<p className="text-closure-check">
					An account closure check for this account has already been issued and will now be replaced:
				</p>
				<KeyValueCardContainer className={"checks"}>
					<div className="is-flex is-align-items-center">
						<KeyValueCard className="old-check check-container is-full-width">
							<div className="old-check check-container is-full-width">
								<div>
									<span>{`Check #${existingCheck.attributes.checkNumber}`}</span>
								</div>
								<div className="date-amount strikethrough">
									<span>{moment(existingCheck.attributes.createdAt).format("L")}</span>
									<span>${(existingCheck.attributes.amount / 100).toFixed(2)}</span>
								</div>
							</div>
						</KeyValueCard>
						<div className="mx-4">
							<Icon icon="arrow-right-chevron" size={24} />
						</div>
						<KeyValueCard className="check-container is-full-width">
							<div className="check-container is-full-width">
								<div>
									<span>New Issued Check</span>
								</div>
								<div className={"date-amount"}>
									<span>{new Date().toLocaleDateString()}</span>
									<span>${(account.attributes.balance / 100).toFixed(2)}</span>
								</div>
							</div>
						</KeyValueCard>
					</div>
				</KeyValueCardContainer>

				<CheckMailingAddress address={updatedAddress} openChangeAddressModal={openChangeAddressModal} />

				<div className="text-closure-check">
					<label className="checkbox">
						<input
							type="checkbox"
							className="mr-2"
							checked={confirmStop}
							onChange={(e) => setConfirmStop(e.target.checked)}
						/>
						By ticking, you are confirming you would like to put a stop payment order on check payment #
						{existingCheck.attributes.checkNumber} and issue a replacement check.{" "}
						<strong>This action is final and cannot be undone.</strong>
					</label>
				</div>

				<div className="modal-actions is-flex is-justify-content-end padding">
					<Button className="button is-light" theme={"neutral"} onClick={closeModal}>
						Cancel
					</Button>
					<Button
						className="button is-danger wider"
						onClick={onSubmit}
						theme="danger"
						disabled={!confirmStop}
						loading={isLoading}
					>
						Stop and Reissue
					</Button>
				</div>
				<div className="modal-actions is-flex is-justify-content-end">
					{successMessage && <p className={"success-message"}>{successMessage}</p>}
					{errorMessage && <p className={"error-message"}>{errorMessage}</p>}
				</div>
			</div>
		</Modal>
	)
}
